import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
  Alert,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  PRODUCT_CONFIG,
  PRODUCT_TAB_ORDER,
  getProductConfig,
} from "configs/products";
import {
  listOndemandRequested,
  selectOndemandList,
  selectOndemandDecommission,
  decommissionOndemandRequested,
  decommissionOndemandCleared,
  resetSaveState,
} from "stores/ondemand/ondemandSlice";
import CreateEnvironment from "./CreateEnvironment";
import SitesTable from "./SitesTable";
import SitesEmptyState from "./SitesEmptyState";

const formatApiError = (error) => {
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error.error === "string") {
    return error.error;
  }
  return JSON.stringify(error);
};

const siteLabel = (site) =>
  site?.org_subdomain || site?.subdomain || site?.hostname || site?.SK;

const EnvironmentsPage = () => {
  const dispatch = useDispatch();
  const [activeProduct, setActiveProduct] = React.useState("enroll");
  const [filterText, setFilterText] = React.useState("");
  const [includeDeleted, setIncludeDeleted] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);

  const productConfig = getProductConfig(activeProduct);
  const supportsListing = Boolean(productConfig?.supportsListing);
  const listState = useSelector((state) =>
    selectOndemandList(state, activeProduct)
  );
  const decommission = useSelector(selectOndemandDecommission);

  const fetchList = React.useCallback(() => {
    if (!supportsListing) {
      return;
    }
    dispatch(
      listOndemandRequested({
        product: activeProduct,
        includeDeleted,
      })
    );
  }, [dispatch, activeProduct, includeDeleted, supportsListing]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleTabChange = (_event, value) => {
    setActiveProduct(value);
    setFilterText("");
  };

  const openCreateDrawer = () => {
    dispatch(resetSaveState());
    setCreateOpen(true);
  };

  const closeCreateDrawer = () => {
    setCreateOpen(false);
  };

  const handleCreateCompleted = () => {
    setCreateOpen(false);
    fetchList();
  };

  const handleDecommission = (site) => {
    dispatch(
      decommissionOndemandRequested({
        product: site.product || activeProduct,
        sk: site.SK,
        label: siteLabel(site),
        includeDeleted,
      })
    );
  };

  const filteredSites = React.useMemo(() => {
    const sites = listState.sites || [];
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return sites;
    }
    return sites.filter((site) => {
      const haystack = [
        site.org_subdomain,
        site.subdomain,
        site.hostname,
        site.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [listState.sites, filterText]);

  const renderInventory = () => {
    if (!supportsListing) {
      const label = productConfig?.label ?? activeProduct;
      return (
        <SitesTable
          sites={[]}
          emptyMessage={`Listing isn't supported for ${label} yet. You can still create a site with New On-Demand Site.`}
        />
      );
    }

    if (listState.listStatus === "loading" && !listState.sites?.length) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      );
    }

    if (listState.listStatus === "failed") {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchList}>
              Retry
            </Button>
          }
        >
          Failed to load sites:{" "}
          {typeof listState.listError === "string"
            ? listState.listError
            : JSON.stringify(listState.listError)}
        </Alert>
      );
    }

    if (!listState.sites?.length) {
      return (
        <SitesEmptyState
          productLabel={productConfig?.label ?? activeProduct}
        />
      );
    }

    if (!filteredSites.length) {
      return (
        <Alert severity="info">No sites match the current filter.</Alert>
      );
    }

    return (
      <SitesTable
        sites={filteredSites}
        onDecommission={handleDecommission}
        decommissioningSk={
          decommission.status === "loading" ? decommission.sk : null
        }
      />
    );
  };

  return (
    <Box px={1} py={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={1}
      >
        <Typography variant="h4" color="primary">
          Environments
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDrawer}
          disableElevation
        >
          New On-Demand Site
        </Button>
      </Box>

      <Tabs
        value={activeProduct}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {PRODUCT_TAB_ORDER.map((product) => (
          <Tab
            key={product}
            value={product}
            label={PRODUCT_CONFIG[product]?.label ?? product}
          />
        ))}
      </Tabs>

      {supportsListing ? (
        <Toolbar
          disableGutters
          sx={{
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
            minHeight: "auto !important",
          }}
        >
          <TextField
            size="small"
            label="Filter"
            placeholder="org, host, owner"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeDeleted}
                onChange={(event) => setIncludeDeleted(event.target.checked)}
                color="primary"
              />
            }
            label="Show deleted"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton
                color="primary"
                onClick={fetchList}
                disabled={listState.listStatus === "loading"}
                size="large"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          {listState.listStatus === "loading" ? (
            <CircularProgress size={20} />
          ) : null}
        </Toolbar>
      ) : null}

      {decommission.status === "loading" ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Decommissioning {decommission.label || "site"}…
        </Alert>
      ) : null}
      {decommission.status === "succeeded" ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => dispatch(decommissionOndemandCleared())}
        >
          Decommissioned {decommission.label || "site"}.
        </Alert>
      ) : null}
      {decommission.status === "failed" ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(decommissionOndemandCleared())}
        >
          Failed to decommission {decommission.label || "site"}:{" "}
          {formatApiError(decommission.error)}
        </Alert>
      ) : null}

      {renderInventory()}

      <Drawer
        anchor="right"
        open={createOpen}
        onClose={closeCreateDrawer}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 480, md: 560 }, p: 2 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h5" color="primary">
            New On-Demand Site
          </Typography>
          <IconButton onClick={closeCreateDrawer} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        {createOpen ? (
          <CreateEnvironment
            embedded
            defaultProduct={activeProduct}
            onCreateCompleted={handleCreateCompleted}
          />
        ) : null}
      </Drawer>
    </Box>
  );
};

export default EnvironmentsPage;
